/**
 * Maintenance mode (#3561, BE ourteam-backend#4630).
 *
 * Two cards, because there are two separate decisions: whether we are in
 * maintenance right now, and who gets told about a window. Flipping the switch
 * does not mail anyone, and mailing does not flip the switch — the notice
 * usually goes out days before the window, and coupling them would either send
 * the warning too late or turn the banner on too early.
 *
 * The switch informs; it does not lock. Nobody is gated out of the app by this
 * page (see the BE ticket's out-of-scope note).
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    AlertTriangleIcon,
    CheckCircle2Icon,
    Loader2Icon,
    SendIcon,
    WrenchIcon,
    XCircleIcon,
} from "lucide-react";
import AuthLayout from "@container/layout/auth";
import { Card, CardContent } from "@component/card";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { Textarea } from "@component/textarea";
import { Switch } from "@component/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@component/alert-dialog";
import CompanyMultiSelect from "./components/company-multi-select";
import {
    formatLocal,
    formatDate,
    formatWindow,
    utcToZonedWallClock,
    zonedWallClockToUtc,
} from "./components/window-format";
import {
    getMaintenanceAdmin,
    sendMaintenanceNotice,
    updateMaintenance,
} from "@service/admin";
import { MaintenanceNotifyResult, MaintenanceState } from "@type/admin";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";

/** Mirrors MAX_NOTIFY_COMPANIES on the server, which REFUSES a larger batch
 *  rather than truncating it. Stopping the selection here is the friendlier
 *  half of the same rule. */
const MAX_COMPANIES = 50;

const BROWSER_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** The zones our workspaces are actually in, plus whatever this browser is set
 *  to, so an operator elsewhere is never forced to do the arithmetic. */
const ZONE_OPTIONS = Array.from(
    new Set([
        BROWSER_ZONE,
        "Asia/Kuala_Lumpur",
        "Asia/Singapore",
        "Asia/Jakarta",
        "Asia/Manila",
        "UTC",
    ])
);

function errorText(err: unknown, fallback: string): string {
    if (axios.isAxiosError(err)) {
        return (
            err.response?.data?.errors?.[0] ||
            err.response?.data?.message ||
            fallback
        );
    }
    return fallback;
}

export default function AdminMaintenancePage() {
    // AuthLayout owns the SnackbarProvider, so useSnackbar must be called from a
    // child rendered *inside* it — not from this top-level page.
    return (
        <AuthLayout>
            <MaintenanceContent />
        </AuthLayout>
    );
}

function MaintenanceContent() {
    const snackbar = useSnackbar();

    // ── switch ────────────────────────────────────────────────────────────────
    const [state, setState] = useState<MaintenanceState | null>(null);
    const [isLoadingState, setIsLoadingState] = useState(true);
    const [isSavingState, setIsSavingState] = useState(false);
    const [pendingActive, setPendingActive] = useState<boolean | null>(null);
    const [message, setMessage] = useState("");
    const [windowZone, setWindowZone] = useState(
        ZONE_OPTIONS.includes("Asia/Kuala_Lumpur") ? "Asia/Kuala_Lumpur" : BROWSER_ZONE
    );
    const [startsLocal, setStartsLocal] = useState("");
    const [endsLocal, setEndsLocal] = useState("");

    // ── notice ────────────────────────────────────────────────────────────────
    const [companyIds, setCompanyIds] = useState<string[]>([]);
    const [noticeZone, setNoticeZone] = useState(
        ZONE_OPTIONS.includes("Asia/Kuala_Lumpur") ? "Asia/Kuala_Lumpur" : BROWSER_ZONE
    );
    const [noticeStartsLocal, setNoticeStartsLocal] = useState("");
    const [noticeEndsLocal, setNoticeEndsLocal] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [confirmSend, setConfirmSend] = useState(false);
    const [result, setResult] = useState<MaintenanceNotifyResult | null>(null);

    const loadState = () => {
        setIsLoadingState(true);
        getMaintenanceAdmin()
            .then((resp) => {
                const data = resp.data?.data ?? null;
                setState(data);
                setMessage(data?.message ?? "");
                if (data?.starts_at) setStartsLocal(utcToZonedWallClock(data.starts_at, windowZone));
                if (data?.ends_at) setEndsLocal(utcToZonedWallClock(data.ends_at, windowZone));
            })
            .catch((err) => {
                snackbar.showError(
                    "Could not read maintenance state",
                    errorText(err, "Please refresh the page and try again.")
                );
                catchErrorLog(`getMaintenanceAdmin error: ${err}`);
            })
            .finally(() => setIsLoadingState(false));
    };

    // Once, on open. `windowZone` is deliberately not a dependency: the typed
    // wall clock belongs to whichever zone is selected, so changing the zone
    // means "18:00 somewhere else", not "re-render this instant". Re-running the
    // load on a zone change would overwrite the operator's edits to say so.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(loadState, []);

    const saveState = async (isActive: boolean) => {
        setIsSavingState(true);
        try {
            const resp = await updateMaintenance({
                is_active: isActive,
                starts_at: startsLocal ? zonedWallClockToUtc(startsLocal, windowZone) : null,
                ends_at: endsLocal ? zonedWallClockToUtc(endsLocal, windowZone) : null,
                message: message.trim() || null,
            });
            if (resp.data?.code && resp.data.code !== 200) {
                snackbar.showError(
                    "Could not save",
                    resp.data?.errors?.[0] || resp.data?.message || "Please try again."
                );
                return;
            }
            snackbar.showSuccess(
                isActive ? "Maintenance mode is on" : "Maintenance mode is off",
                isActive
                    ? "Everyone signed in now sees the notice."
                    : "The notice is no longer shown."
            );
            loadState();
        } catch (err) {
            snackbar.showError("Could not save", errorText(err, "Please try again."));
            catchErrorLog(`updateMaintenance error: ${err}`);
        } finally {
            setIsSavingState(false);
        }
    };

    const noticeStartsUtc = useMemo(
        () => (noticeStartsLocal ? zonedWallClockToUtc(noticeStartsLocal, noticeZone) : null),
        [noticeStartsLocal, noticeZone]
    );
    const noticeEndsUtc = useMemo(
        () => (noticeEndsLocal ? zonedWallClockToUtc(noticeEndsLocal, noticeZone) : null),
        [noticeEndsLocal, noticeZone]
    );
    const endsBeforeStarts =
        !!noticeStartsUtc && !!noticeEndsUtc && noticeEndsUtc <= noticeStartsUtc;

    const canSend =
        companyIds.length > 0 && !!noticeStartsUtc && !endsBeforeStarts && !isSending;

    const sendNotice = async () => {
        if (!canSend || !noticeStartsUtc) return;
        setIsSending(true);
        setResult(null);
        try {
            const resp = await sendMaintenanceNotice({
                company_ids: companyIds,
                scheduled_at: noticeStartsUtc,
                ends_at: noticeEndsUtc,
                timezone: noticeZone,
            });
            const data = resp.data?.data ?? null;
            if (!data) {
                snackbar.showError("Could not send", "The server returned nothing to report.");
                return;
            }
            setResult(data);
            if (data.failed > 0) {
                snackbar.showError(
                    "Sent with failures",
                    `${data.sent} delivered, ${data.failed} failed. See the breakdown below.`
                );
            } else {
                snackbar.showSuccess(
                    "Notice sent",
                    `${data.sent} recipient${data.sent === 1 ? "" : "s"} across ${companyIds.length} workspace${companyIds.length === 1 ? "" : "s"}.`
                );
            }
        } catch (err) {
            snackbar.showError("Could not send", errorText(err, "Please try again."));
            catchErrorLog(`sendMaintenanceNotice error: ${err}`);
        } finally {
            setIsSending(false);
            setConfirmSend(false);
        }
    };

    const isActive = !!state?.is_active;

    return (
        <div className="space-y-6 pb-10">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
                    <WrenchIcon className="h-5 w-5" />
                </div>
                <div>
                    <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Maintenance</h1>
                    <p className="text-sm text-neutral-500">
                        Say that the platform is under maintenance, and warn the workspaces it affects.
                    </p>
                </div>
            </div>

            {/* ── Card 1: the switch ─────────────────────────────────────────── */}
            <Card className="max-w-3xl">
                <CardContent className="space-y-6 p-6 sm:p-8">
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <h2 className="my-0 text-base font-semibold text-gray-900">Maintenance status</h2>
                            {isLoadingState ? (
                                <p className="my-0 mt-1 text-sm text-neutral-500">Reading current state…</p>
                            ) : isActive ? (
                                <p className="my-0 mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-amber-700">
                                    <AlertTriangleIcon className="h-4 w-4" />
                                    On — everyone signed in sees the notice
                                </p>
                            ) : (
                                <p className="my-0 mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700">
                                    <CheckCircle2Icon className="h-4 w-4" />
                                    Off — nothing is shown to customers
                                </p>
                            )}
                            {!isLoadingState && (state?.updated_at || state?.updated_by_name) && (
                                <p className="my-0 mt-1 text-xs text-neutral-500">
                                    Last changed {formatLocal(state?.updated_at ?? null)}
                                    {state?.updated_by_name ? ` by ${state.updated_by_name}` : ""}
                                </p>
                            )}
                        </div>
                        {/* The switch opens a confirmation rather than acting. This is
                            customer-visible either way round: turning it on shows an
                            outage notice to everyone, turning it off takes down a
                            warning that may still be true. */}
                        <Switch
                            checked={isActive}
                            disabled={isLoadingState || isSavingState}
                            onCheckedChange={(next) => setPendingActive(next)}
                            aria-label="Maintenance mode"
                        />
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <div className="space-y-2">
                            <Label className="block text-sm font-medium text-gray-900">Timezone</Label>
                            <Select value={windowZone} onValueChange={setWindowZone}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {ZONE_OPTIONS.map((z) => (
                                        <SelectItem key={z} value={z}>{z}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label className="block text-sm font-medium text-gray-900">Starts (optional)</Label>
                            <Input
                                type="datetime-local"
                                value={startsLocal}
                                onChange={(e) => setStartsLocal(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="block text-sm font-medium text-gray-900">Ends (optional)</Label>
                            <Input
                                type="datetime-local"
                                value={endsLocal}
                                onChange={(e) => setEndsLocal(e.target.value)}
                            />
                        </div>
                    </div>
                    <p className="my-0 text-xs text-neutral-500">
                        Times are read in the timezone above, not this browser&rsquo;s. Leaving
                        &ldquo;Ends&rdquo; empty is fine — the notice then says work is in progress
                        without naming a finish.
                    </p>

                    <div className="space-y-2">
                        <Label className="block text-sm font-medium text-gray-900">Message (optional)</Label>
                        <Textarea
                            rows={2}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Scheduled database upgrade. Sign-in and applications are unaffected."
                        />
                        <p className="my-0 text-xs text-neutral-500">
                            Shown in the banner. Plain text — say what is affected, not just that
                            something is.
                        </p>
                    </div>

                    <div className="flex justify-end">
                        <Button
                            variant="outline"
                            disabled={isLoadingState || isSavingState}
                            onClick={() => void saveState(isActive)}
                        >
                            {isSavingState && <Loader2Icon className="h-4 w-4 animate-spin" />}
                            Save window and message
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* ── Card 2: the notice ─────────────────────────────────────────── */}
            <Card className="max-w-3xl">
                <CardContent className="space-y-6 p-6 sm:p-8">
                    <div>
                        <h2 className="my-0 text-base font-semibold text-gray-900">Notify workspaces</h2>
                        <p className="my-0 mt-1 text-sm text-neutral-500">
                            Emails every verified, active HR user in the workspaces you pick. This does
                            not change the switch above.
                        </p>
                    </div>

                    <div className="space-y-2">
                        <Label className="block text-sm font-medium text-gray-900">Workspaces</Label>
                        <CompanyMultiSelect
                            value={companyIds}
                            onChange={setCompanyIds}
                            disabled={isSending}
                            max={MAX_COMPANIES}
                        />
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <div className="space-y-2">
                            <Label className="block text-sm font-medium text-gray-900">Timezone</Label>
                            <Select value={noticeZone} onValueChange={setNoticeZone} disabled={isSending}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {ZONE_OPTIONS.map((z) => (
                                        <SelectItem key={z} value={z}>{z}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label className="block text-sm font-medium text-gray-900">Starts</Label>
                            <Input
                                type="datetime-local"
                                value={noticeStartsLocal}
                                onChange={(e) => setNoticeStartsLocal(e.target.value)}
                                disabled={isSending}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="block text-sm font-medium text-gray-900">Ends (optional)</Label>
                            <Input
                                type="datetime-local"
                                value={noticeEndsLocal}
                                onChange={(e) => setNoticeEndsLocal(e.target.value)}
                                disabled={isSending}
                            />
                        </div>
                    </div>
                    {endsBeforeStarts && (
                        <p className="my-0 text-xs text-rose-600">
                            The window ends before it starts.
                        </p>
                    )}

                    {/* The preview. A wrong date is cheap to catch here and expensive
                        to catch in an inbox. The server renders the copy that ships,
                        and the result panel below shows what it actually sent. */}
                    {noticeStartsUtc && !endsBeforeStarts && (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                            <p className="my-0 text-xs font-semibold uppercase tracking-wider text-gray-500">
                                Preview
                            </p>
                            <dl className="mt-2 space-y-1.5 text-sm">
                                <div className="flex gap-2">
                                    <dt className="w-40 flex-shrink-0 text-neutral-500">companyName</dt>
                                    <dd className="my-0 text-gray-900">each workspace&rsquo;s own name</dd>
                                </div>
                                <div className="flex gap-2">
                                    <dt className="w-40 flex-shrink-0 text-neutral-500">maintenanceDate</dt>
                                    <dd className="my-0 text-gray-900">{formatDate(noticeStartsUtc, noticeZone)}</dd>
                                </div>
                                <div className="flex gap-2">
                                    <dt className="w-40 flex-shrink-0 text-neutral-500">maintenanceDateTime</dt>
                                    <dd className="my-0 text-gray-900">
                                        {formatWindow(noticeStartsUtc, noticeEndsUtc, noticeZone)}
                                    </dd>
                                </div>
                            </dl>
                            <p className="my-0 mt-2 text-xs text-neutral-500">
                                The server renders the final wording; this is how it will read.
                            </p>
                        </div>
                    )}

                    <div className="flex justify-end">
                        <Button disabled={!canSend} onClick={() => setConfirmSend(true)}>
                            {isSending ? (
                                <Loader2Icon className="h-4 w-4 animate-spin" />
                            ) : (
                                <SendIcon className="h-4 w-4" />
                            )}
                            {isSending ? "Sending…" : "Send notice"}
                        </Button>
                    </div>

                    {/* What actually happened, per workspace. A flat "Sent!" over a
                        response saying three workspaces had no reachable HR user
                        would be a lie the operator only discovers from support. */}
                    {result && (
                        <div className="space-y-2 border-t border-gray-100 pt-4">
                            <p className="my-0 text-sm font-medium text-gray-900">
                                {result.sent} delivered
                                {result.failed > 0 ? `, ${result.failed} failed` : ""}
                            </p>
                            <p className="my-0 text-xs text-neutral-500">
                                Sent as &ldquo;{result.maintenance_date_time}&rdquo;.
                            </p>
                            <ul className="my-0 divide-y divide-gray-100 rounded-lg border border-gray-200 p-0">
                                {result.results.map((row) => (
                                    <li
                                        key={row.company_id}
                                        className="flex items-start justify-between gap-3 px-3 py-2 text-sm"
                                    >
                                        <span className="min-w-0">
                                            <span className="block truncate text-gray-900">
                                                {row.company_name || row.company_id}
                                            </span>
                                            {row.reason && (
                                                <span className="block text-xs text-amber-700">{row.reason}</span>
                                            )}
                                        </span>
                                        <span className="flex flex-shrink-0 items-center gap-1.5 text-xs">
                                            {row.sent > 0 && (
                                                <span className="inline-flex items-center gap-1 text-emerald-700">
                                                    <CheckCircle2Icon className="h-3.5 w-3.5" />
                                                    {row.sent}
                                                </span>
                                            )}
                                            {row.failed > 0 && (
                                                <span className="inline-flex items-center gap-1 text-rose-600">
                                                    <XCircleIcon className="h-3.5 w-3.5" />
                                                    {row.failed}
                                                </span>
                                            )}
                                            {row.recipients === 0 && (
                                                <span className="text-neutral-500">no recipients</span>
                                            )}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Confirm: toggle */}
            <AlertDialog
                open={pendingActive !== null}
                onOpenChange={(open) => !open && setPendingActive(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {pendingActive ? "Turn maintenance mode on?" : "Turn maintenance mode off?"}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {pendingActive
                                ? "Everyone signed in sees a maintenance notice immediately. Nobody is locked out — this states that we are working on the platform, it does not block anything."
                                : "The notice disappears for everyone immediately. Turn it off only once the work is genuinely finished."}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                const next = pendingActive;
                                setPendingActive(null);
                                if (next !== null) void saveState(next);
                            }}
                        >
                            {pendingActive ? "Turn on" : "Turn off"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Confirm: send. Names the count — this is real mail to real customers. */}
            <AlertDialog open={confirmSend} onOpenChange={setConfirmSend}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Send the notice to {companyIds.length} workspace
                            {companyIds.length === 1 ? "" : "s"}?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Every verified, active HR user in those workspaces is emailed
                            {noticeStartsUtc
                                ? ` about ${formatWindow(noticeStartsUtc, noticeEndsUtc, noticeZone)}`
                                : ""}
                            . This cannot be recalled.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isSending}>Cancel</AlertDialogCancel>
                        <AlertDialogAction disabled={isSending} onClick={() => void sendNotice()}>
                            Send
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
